<script setup>
import { watch, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useTipStore } from "@/store/tip-store";
import { useTwitchStore } from "@/store/twitch-store";
import AOS from "aos";

const route = useRoute();
const hideHeaderFooter = ref(false);
const tipStore = useTipStore();
const twitchStore = useTwitchStore();

watch(
  route,
  (to) => {
    hideHeaderFooter.value = to.meta.hideHeaderFooter ?? false;
  },
  { immediate: true }
);

const tipSet = computed(() => {
  return tipStore.state.tipSet;
});

const mainClass = computed(() => {
  let defaultClass = "bg-dark text-white";
  if (hideHeaderFooter.value) {
    defaultClass = "container-fluid py-0 my-0 mx-0 px-0";
  }
  return defaultClass;
});

const isTwitchLive = computed(() => {
  return twitchStore.isLive;
});

const twitchStream = computed(() => {
  return twitchStore.stream;
});

onMounted(() => {
  AOS.init({
    duration: 800,
    once: true,
  });
  twitchStore.checkLive();
});

function trackEvent(event) {
  if (import.meta.env.MODE == "production") {
    window._paq.push(["trackEvent", "link", event]);
  }

  return true;
}
</script>

<template>
  <!-- =====================================================
         NAVIGATION
    ====================================================== -->

  <nav
    v-if="!hideHeaderFooter"
    class="navbar navbar-expand-lg sticky-top dj-navbar"
  >
    <div class="container">
      <!-- LOGO -->

      <routerLink class="navbar-brand" to="/">
        <img
          class="navbar-brand-logo"
          src="/assets/images/djsaturn-logo.svg"
          alt="DJ Saturn"
        />
      </routerLink>

      <!-- MOBILE CONTROLS -->

      <div class="d-flex align-items-center gap-2 d-lg-none">
        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>
      </div>

      <!-- NAV LINKS -->

      <div id="navbarSupportedContent" class="collapse navbar-collapse">
        <ul class="navbar-nav ms-auto align-items-lg-center mb-2 mb-lg-0">
          <!-- HOME -->

          <li class="nav-item">
            <routerLink
              class="nav-link"
              :class="{
                active: route.name === 'home',
              }"
              :to="{ name: 'home' }"
            >
              Home
            </routerLink>
          </li>

          <!-- <li class="nav-item">
            <routerLink
              class="nav-link"
              :to="{ name: 'home', hash: '#events' }"
            >
              Events
            </routerLink>
          </li> -->

          <!-- MIXES -->

          <li class="nav-item">
            <routerLink
              class="nav-link"
              :to="{
                name: 'home',
                hash: '#videoMixes',
              }"
            >
              Mixes
            </routerLink>
          </li>

          <!-- CONTACT -->

          <li class="nav-item">
            <routerLink
              class="nav-link d-none d-lg-inline"
              :to="{
                name: 'home',
                hash: '#contact-desktop',
              }"
            >
              Contact
            </routerLink>
            <routerLink
              class="nav-link d-lg-none"
              :to="{
                name: 'home',
                hash: '#contact-mobile',
              }"
            >
              Contact
            </routerLink>
          </li>

          <!-- TIP -->

          <li class="nav-item">
            <routerLink
              class="nav-link nav-tip"
              :class="{
                active: route.name === 'tip',
              }"
              :to="{ name: 'tip' }"
            >
              <i class="bi bi-heart-fill me-2"></i>
              Tip Jar

              <span
                v-if="tipSet"
                class="badge rounded-pill text-bg-success ms-1"
              >
                1
              </span>
            </routerLink>
          </li>
        </ul>

        <!-- TWITCH -->

        <a
          v-if="isTwitchLive"
          href="https://www.twitch.tv/djsaturn_"
          target="_blank"
          rel="noopener"
          class="btn btn-primary ms-lg-3 mt-3 mt-lg-0 live-button"
          @click="trackEvent('Header-Twitch')"
        >
          <span class="live-dot me-2 mb-1"></span>
          Watch Live
          <span
            v-if="twitchStream?.viewers !== undefined"
            class="live-viewers ms-2"
          >
            <i class="bi bi-eye-fill me-1"></i>
            {{ twitchStream.viewers.toLocaleString() }}
          </span>
        </a>
      </div>
    </div>
  </nav>

  <!-- =====================================================
         PAGE
    ====================================================== -->

  <main :class="mainClass">
    <router-view />
  </main>

  <!-- =====================================================
         FOOTER
    ====================================================== -->

  <footer v-if="!hideHeaderFooter" class="dj-footer mt-auto">
    <div class="container">
      <!-- MAIN FOOTER -->

      <div class="row gy-4 align-items-center">
        <!-- LOGO -->

        <div class="col-lg-4">
          <routerLink to="/" class="footer-logo">
            <img src="/assets/images/djsaturn-logo.svg" alt="DJ Saturn" />
          </routerLink>

          <p class="footer-tagline">Music. Energy. Community.</p>
        </div>

        <!-- LINKS -->

        <div class="col-lg-4">
          <div class="footer-links justify-content-lg-center">
            <routerLink :to="{ name: 'home' }"> Home </routerLink>

            <routerLink
              :to="{
                name: 'home',
                hash: '#videoMixes',
              }"
            >
              Mixes
            </routerLink>

            <routerLink
              :to="{
                name: 'home',
                hash: '#contact',
              }"
            >
              Contact
            </routerLink>

            <routerLink :to="{ name: 'tip' }"> Tip </routerLink>
          </div>
        </div>

        <!-- SOCIAL -->

        <div class="col-lg-4">
          <div class="footer-social justify-content-lg-end">
            <a
              href="https://www.instagram.com/djs4turn"
              target="_blank"
              rel="noopener"
              aria-label="Instagram"
              @click="trackEvent('Footer-Instagram')"
            >
              <i class="bi bi-instagram"></i>
            </a>

            <a
              href="https://twitter.com/djsaturn"
              target="_blank"
              rel="noopener"
              aria-label="X / Twitter"
              @click="trackEvent('Footer-Twitter')"
            >
              <i class="bi bi-twitter-x"></i>
            </a>

            <a
              href="https://www.youtube.com/djs4turn"
              target="_blank"
              rel="noopener"
              aria-label="YouTube"
              @click="trackEvent('Footer-Youtube')"
            >
              <i class="bi bi-youtube"></i>
            </a>

            <a
              href="https://www.twitch.tv/djsaturn_"
              target="_blank"
              rel="noopener"
              aria-label="Twitch"
              @click="trackEvent('Footer-Twitch')"
            >
              <i class="bi bi-twitch"></i>
            </a>

            <a
              href="https://discord.gg/K6dnWxdDnu"
              target="_blank"
              rel="noopener"
              aria-label="Discord"
              @click="trackEvent('Footer-Discord')"
            >
              <i class="bi bi-discord"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- COPYRIGHT -->

      <div class="footer-bottom">
        <div>
          © {{ new Date().getFullYear() }}
          DJ Saturn. All rights reserved.
        </div>

        <a
          v-if="isTwitchLive"
          href="https://www.twitch.tv/djsaturn_"
          target="_blank"
          rel="noopener"
          @click="trackEvent('Footer-Watch-Twitch')"
        >
          <span class="live-dot"></span>

          Watch on Twitch
        </a>
      </div>
    </div>
  </footer>
</template>

<style scoped>
/* ==========================================================
   NAVBAR
========================================================== */

.dj-navbar {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid var(--bs-border-color);
  background: rgba(var(--bs-body-bg-rgb), 0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

/* ==========================================================
   LOGO
========================================================== */

.navbar-brand-logo {
  width: 10rem;
  height: auto;
}

/* ==========================================================
   NAV LINKS
========================================================== */

.nav-link {
  position: relative;

  padding-right: 0.85rem !important;
  padding-left: 0.85rem !important;

  font-size: 0.8rem;
  font-weight: 700;

  letter-spacing: 0.06rem;
  text-transform: uppercase;
}

.nav-link::after {
  content: "";

  position: absolute;

  right: 0.85rem;
  bottom: 0;
  left: 0.85rem;

  height: 2px;

  background: var(--bs-primary);

  transform: scaleX(0);
  transform-origin: left;

  transition: transform 0.25s ease;
}

.nav-link:hover::after,
.nav-link.active::after {
  transform: scaleX(1);
}

/* ==========================================================
   THEME TOGGLE
========================================================== */

.theme-toggle {
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;

  padding: 0;

  border: 1px solid var(--bs-border-color);

  border-radius: 50%;

  color: var(--bs-body-color);

  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.theme-toggle:hover {
  border-color: var(--bs-primary);

  color: var(--bs-primary);

  transform: rotate(8deg);
}

/* ==========================================================
   MAIN
========================================================== */

.dj-main {
  min-height: 70vh;

  background: var(--bs-body-bg);

  color: var(--bs-body-color);
}

/* ==========================================================
   FOOTER
========================================================== */

.dj-footer {
  padding: 4rem 0 2rem;

  border-top: 1px solid var(--bs-border-color);

  background: var(--bs-body-bg);

  color: var(--bs-body-color);
}

/* ==========================================================
   FOOTER LOGO
========================================================== */

.footer-logo {
  display: inline-block;
}

.footer-logo img {
  width: 10rem;
  height: auto;
}

.footer-tagline {
  margin-top: 0.75rem;
  margin-bottom: 0;

  color: var(--bs-secondary-color);

  font-size: 0.9rem;
}

/* ==========================================================
   FOOTER LINKS
========================================================== */

.footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.footer-links a {
  color: var(--bs-secondary-color);

  font-size: 0.75rem;
  font-weight: 700;

  letter-spacing: 0.05rem;
  text-decoration: none;
  text-transform: uppercase;

  transition: color 0.2s ease;
}

.footer-links a:hover {
  color: var(--bs-primary);
}

/* ==========================================================
   SOCIAL
========================================================== */

.footer-social {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.footer-social a {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 42px;
  height: 42px;

  border: 1px solid var(--bs-border-color);

  border-radius: 50%;

  color: var(--bs-body-color);

  text-decoration: none;

  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, transform 0.2s ease;
}

.footer-social a:hover {
  border-color: var(--bs-primary);

  background: var(--bs-primary);

  color: #fff;

  transform: translateY(-3px);
}

/* ==========================================================
   FOOTER BOTTOM
========================================================== */

.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: 3rem;
  padding-top: 1.5rem;

  border-top: 1px solid var(--bs-border-color);

  color: var(--bs-secondary-color);

  font-size: 0.8rem;
}

.footer-bottom a {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  color: var(--bs-secondary-color);

  text-decoration: none;
}

.footer-bottom a:hover {
  color: var(--bs-primary);
}

.live-dot {
  display: inline-block;

  width: 7px;
  height: 7px;

  border-radius: 50%;

  background: #dc3545;

  box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.12);
}

.live-viewers {
  padding-left: 0.65rem;

  border-left: 1px solid rgba(255, 255, 255, 0.3);

  font-size: 0.75rem;
  font-weight: 600;

  opacity: 0.85;
}

/* ==========================================================
   MOBILE
========================================================== */

@media (max-width: 991.98px) {
  .navbar-collapse {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.5rem;

    border-top: 1px solid var(--bs-border-color);
  }

  .nav-link {
    padding: 0.75rem 0 !important;
  }

  .nav-link::after {
    display: none;
  }
}

@media (max-width: 767.98px) {
  .dj-footer {
    padding-top: 3rem;
  }

  .footer-bottom {
    align-items: flex-start;
    flex-direction: column;

    gap: 0.75rem;
  }
}

.nav-tip {
  display: inline-flex;
  align-items: center;

  padding: 0.45rem 1rem !important;

  color: var(--bs-primary) !important;

  border: 1px solid var(--bs-primary);
  border-radius: 50rem;

  font-weight: 700;

  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;
}

.nav-tip:hover,
.nav-tip.router-link-active {
  color: #fff !important;
  background: var(--bs-primary);

  box-shadow: 0 0 18px rgba(var(--bs-primary-rgb), 0.35);

  transform: translateY(-1px);
}

.nav-tip i {
  font-size: 0.85em;
}
</style>
